import type { SnipcatValues } from "./types";

/**
 * Coarse description of the visitor's device, sent once per page as
 * `device_*` values.
 *
 * Coarse on purpose: the categories below (phone/desktop, OS family, browser
 * family, viewport) are what a funnel question needs - "do mobile visitors
 * from the ad drop at the pricing?" - and nothing here is precise enough to
 * fingerprint anybody. The raw user agent is deliberately NOT sent: it is the
 * one string that identifies a browser install, and the session already
 * outlives the person's erasure (see values.ts).
 *
 * `device_inapp` matters most for paid social: an Instagram or Facebook ad
 * opens in the app's own webview, which has no shared cookies, no password
 * manager and no "open in browser" unless the visitor asks for it - a
 * different conversion rate is expected there, and without this key it would
 * just look like "mobile converts badly".
 */

const detectOs = (ua: string, touchPoints: number): string => {
  if (/iPhone|iPad|iPod/.test(ua)) {
    return "ios";
  }
  // iPadOS 13+ calls itself a Mac; the touch screen gives it away.
  if (/Macintosh/.test(ua) && touchPoints > 1) {
    return "ios";
  }
  if (/Android/.test(ua)) {
    return "android";
  }
  if (/Windows/.test(ua)) {
    return "windows";
  }
  if (/Mac OS X|Macintosh/.test(ua)) {
    return "macos";
  }
  if (/CrOS/.test(ua)) {
    return "chromeos";
  }
  if (/Linux/.test(ua)) {
    return "linux";
  }
  return "other";
};

/** Order matters - most browsers also claim to be Chrome and Safari. */
const detectBrowser = (ua: string, os: string): string => {
  if (/Edg(e|A|iOS)?\//.test(ua)) {
    return "edge";
  }
  if (/SamsungBrowser\//.test(ua)) {
    return "samsung";
  }
  if (/OPR\/|Opera/.test(ua)) {
    return "opera";
  }
  if (/Firefox\/|FxiOS\//.test(ua)) {
    return "firefox";
  }
  if (/Chrome\/|CriOS\//.test(ua)) {
    return "chrome";
  }
  if (/Safari\//.test(ua)) {
    return "safari";
  }
  // Every browser and webview on iOS is WebKit; in-app browsers just omit
  // the Safari token.
  return os === "ios" ? "safari" : "other";
};

/** The app whose embedded browser is showing the page, if it is one. */
const detectInApp = (ua: string): string | null => {
  if (/Instagram/.test(ua)) {
    return "instagram";
  }
  if (/FB_IAB|FBAN|FBAV/.test(ua)) {
    return "facebook";
  }
  if (/Messenger/.test(ua)) {
    return "messenger";
  }
  if (/TikTok|BytedanceWebview/.test(ua)) {
    return "tiktok";
  }
  if (/LinkedInApp/.test(ua)) {
    return "linkedin";
  }
  if (/Snapchat/.test(ua)) {
    return "snapchat";
  }
  if (/; wv\)/.test(ua)) {
    return "webview";
  }
  return null;
};

const detectType = (
  ua: string,
  os: string,
  touchPoints: number,
  uaDataMobile: boolean | undefined,
): string => {
  if (os === "ios") {
    return /iPad/.test(ua) || /Macintosh/.test(ua) ? "tablet" : "mobile";
  }
  if (os === "android") {
    return /Mobile/.test(ua) ? "mobile" : "tablet";
  }
  if (uaDataMobile === true || /Mobi/.test(ua)) {
    return "mobile";
  }
  // a touch-only non-phone of another OS is most likely a tablet
  if (touchPoints > 1 && /Tablet|Touch/.test(ua)) {
    return "tablet";
  }
  return "desktop";
};

export const describeDevice = (): SnipcatValues => {
  if (typeof navigator === "undefined" || typeof window === "undefined") {
    return {};
  }
  const nav = navigator as Navigator & {
    userAgentData?: { mobile?: boolean };
  };
  const ua = nav.userAgent || "";
  const touchPoints = nav.maxTouchPoints || 0;
  const os = detectOs(ua, touchPoints);
  const values: SnipcatValues = {
    device_type: detectType(ua, os, touchPoints, nav.userAgentData?.mobile),
    device_os: os,
    device_browser: detectBrowser(ua, os),
  };
  const inApp = detectInApp(ua);
  if (inApp) {
    values.device_inapp = inApp;
  }
  if (window.innerWidth && window.innerHeight) {
    values.device_viewport = window.innerWidth + "x" + window.innerHeight;
  }
  if (window.screen && window.screen.width && window.screen.height) {
    values.device_screen = window.screen.width + "x" + window.screen.height;
  }
  if (window.devicePixelRatio) {
    values.device_dpr = Math.round(window.devicePixelRatio * 100) / 100;
  }
  if (nav.language) {
    values.device_lang = String(nav.language).slice(0, 16);
  }
  return values;
};
